<template>
  <modal
    class="custom-modal"
    v-model="modalOpened"
    effect="fade/zoom"
    cancelText="Fermer"
    okText="Enregistrer"
    :backdrop="false"
  >
    <div slot="modal-header" class="modal-header row m-0">
      <h4 class="modal-title">Création d'une attribution</h4>
    </div>

    <Loader :is-visible="loading" />

    <div class="form-group">
      <label>Client</label>
      <v-select
        v-model="clientSelected"
        placeholder="Sélectionner un client"
        :options="clientsFound"
        :disabled="!!client"
        @search="searchClients"
        @input="getClientOrders"
      ></v-select>
    </div>

    <div v-if="clientSelected" class="form-group">
      <label>Commande</label>
      <v-select
        v-model="selectedOrder"
        placeholder="Sélectionner une commande"
        :options="ordersOptions"
        :disabled="!!client && !!order"
      ></v-select>
    </div>

    <div v-if="selectedOrder" class="form-group">
      <label>Détails d'attribution</label>
      <el-input v-model="selectedOrder.appNotes" type="textarea" :rows="8" />
    </div>

    <div slot="modal-footer" class="modal-footer">
      <button class="btn" @click="closeModal">Fermer</button>

      <button
        v-if="selectedOrder"
        class="btn btn-primary"
        @click="handleSuccessNewOrder"
      >
        Enregister
      </button>
    </div>
  </modal>
</template>

<script>
import debounce from "lodash.debounce";
import Modal from "vue-strap/src/Modal";

import Loader from "@/components/utils/Loader";

export default {
  components: {
    Modal,
    Loader,
  },

  props: {
    isShow: {
      type: Boolean,
      required: true,
    },

    client: {
      type: Object,
      default: null,
    },

    order: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      modalOpened: false,
      clientSelected: undefined,
      clientsFound: [],
      ordersOptions: [],
      selectedOrder: undefined,
    };
  },

  watch: {
    isShow(newVal) {
      this.modalOpened = newVal;
    },

    client() {
      this.initializeData();
    },

    order() {
      this.initializeData();
    },
  },

  created() {
    this.modalOpened = this.isShow;

    this.initializeData();
  },

  methods: {
    handleSuccessNewOrder() {
      if (!this.selectedOrder?.id) {
        this.$toast.error("Veuillez sélectionner une commande");
        return;
      }

      this.$confirm({
        message: `Vous allez créer une nouvelle attribution pour ${this.clientSelected.label} ?`,
        buttonTrueText: "Confirmer",
        buttonFalseText: "Annuler",
        onConfirm: async () => {
          try {
            this.loading = true;
            const { data } = await this.$api.put(
              `/orders/attribution/${this.selectedOrder.id}`,
              { appNotes: this.selectedOrder.appNotes },
            );

            this.$emit("created", data.attribution);

            const routeName =
              data.price > 0 ? "order-details" : "attributions-order";

            if (this.$route.name !== routeName) {
              const route = this.$router.resolve({
                name: routeName,
                params: { id: data.id },
              });

              window.open(route.href, "_blank");
            } else {
              window.location.reload();
            }

            this.$toast.success("Attribution créée avec succès");
          } catch (e) {
            this.$errorToast("Erreur lors de la création de l'attribution");
          } finally {
            this.loading = false;
            this.clientSelected = undefined;
            this.selectedOrder = undefined;
            this.$emit("close");
          }
        },
      });
    },

    closeModal() {
      if (this.selectedOrder) {
        this.$confirm({
          message:
            "Si vous fermez cette modal, l'attribution ne sera pas créée.",
          buttonTrueText: "Confirmer",
          buttonFalseText: "Annuler",
          onConfirm: () => {
            this.$emit("close");
          },
        });

        return;
      }

      this.clientSelected = undefined;
      this.selectedOrder = undefined;
      this.$emit("close");
    },

    async searchClients(search, loading) {
      if (search !== "") {
        await this.fetchClients(search, loading, this);
      }
    },

    fetchClients: debounce(async (search, loading, vm) => {
      if (!search || search.lentgh < 3) {
        return;
      }

      loading(true);

      const { data } = await vm.$api.get("/clients/filter", {
        params: { q: search },
      });

      vm.clientsFound = data.map((client) => ({
        label: `${client.company || client.name} (${
          client.contactEmail || client.email
        })`,
        value: client.email,
        id: client.id,
      }));

      loading(false);

      return vm.clientsFound;
    }, 300),

    async getClientOrders(e) {
      this.selectedOrder = undefined;
      this.ordersOptions = [];

      try {
        this.loading = true;

        const { data } = await this.$api.get(`/orders/clients/${e.id}`);

        if (!data || data.length === 0) {
          this.$toast.warning("Aucune commande trouvée pour ce client");
          return;
        }

        this.ordersOptions = data.map((order) => ({
          ...order,
          id: order.id,
          value: order.id,
          label: `${
            order.label ? order.label + " - " : ""
          }Commande du ${this.formatDate(order.createdAt)} de ${
            order.session
          } séance${order.session > 1 ? "s" : ""}`,
        }));

        /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
      } catch (_) {
      } finally {
        this.loading = false;
      }
    },

    initializeData() {
      if (this.client) {
        this.clientSelected = {
          label: `${
            this.client.fullName || this.client.company || this.client.name
          } (${this.client.contactEmail || this.client.email})`,
          value: this.client.email,
          id: this.client.id,
        };

        if (this.order) {
          this.ordersOptions = [
            {
              ...this.order,
              value: this.order.id,
              label: `${
                this.order.label ? this.order.label + " - " : ""
              }Commande du ${this.formatDate(this.order.createdAt)} de ${
                this.order.session
              } séance${this.order.session > 1 ? "s" : ""}`,
            },
          ];
          this.selectedOrder = this.ordersOptions[0];
        } else {
          this.getClientOrders({ id: this.client.id });
        }
      }
    },
  },
};
</script>
